import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./leaderboard.css";
import { fetchLeaderboard } from "../../scoreservice";

function getDisplayName(row) {
  const name = String(row?.name ?? "").trim();
  if (name && name.toLowerCase() !== "null null") return name;
  const email = String(row?.email ?? "").trim();
  if (email) return email.split("@")[0] || "Player";
  return "Player";
}

function Leaderboard({ updateHeader }) {
  const navigate = useNavigate();
  const [themeData, setThemeData] = useState(null);
  const [topFive, setTopFive] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [textColor, setTextColor] = useState("#333");

  useEffect(() => {
    updateHeader({ backButtonRedirect: "/thankyou" });
  }, []);

  useEffect(() => {
    const storedSession = sessionStorage.getItem("userData");
    const storedTheme = sessionStorage.getItem("themeData");

    if (!storedSession || !storedTheme) {
      navigate("/login");
      return;
    }

    const parsedUser = JSON.parse(storedSession);
    const parsedTheme = JSON.parse(storedTheme);
    setThemeData(parsedTheme);
    setTextColor(parsedTheme.landing_page_title_color || "#333");

    const loadLeaderboard = async () => {
      setLoading(true);
      setError(null);

      const result = await fetchLeaderboard({
        sessionId: parsedUser.sessionId,
        organizationId: parsedUser.organizationId,
        userId: parsedUser.userId || parsedUser.userid,
      });

      if (result?.status === "success" && result?.data) {
        setTopFive(Array.isArray(result.data.topFive) ? result.data.topFive : []);
        setCurrentUser(result.data.currentUser || null);
      } else {
        setError(result?.message || "Failed to load leaderboard");
        setTopFive([]);
        setCurrentUser(null);
      }

      setLoading(false);
    };

    loadLeaderboard();
  }, [navigate]);

  useEffect(() => {
    if (!themeData) return;

    const body = document.body;
    if (body.classList.contains("common-bg")) {
      if (window.innerWidth <= 768) {
        body.style.backgroundImage = `url(${themeData.background_mob})`;
      } else {
        body.style.backgroundImage = `url(${themeData.background_desk})`;
      }
    }
  }, [themeData]);

  const showCurrentUserRow =
    currentUser && Number(currentUser.rank) > 5;

  const getRowClass = (rank, isYou) => {
    if (isYou) return "lb-row-you";
    if (rank === 1) return "lb-row-gold";
    if (rank === 2) return "lb-row-silver";
    if (rank === 3) return "lb-row-bronze";
    return "";
  };

  const currentUserId = (() => {
    try {
      const u = JSON.parse(sessionStorage.getItem("userData") || "{}");
      const id = String(u.userId || u.userid || "").trim();
      const underscore = id.indexOf("_");
      return underscore > 0 ? id.slice(0, underscore) : id;
    } catch {
      return "";
    }
  })();

  return (
    <div className="lb-page">
      <h2 className="lb-title" style={{ color: textColor }}>
        Leaderboard
      </h2>

      <div className="lb-card">
        {loading ? (
          <p className="lb-message" style={{ color: textColor }}>
            Loading leaderboard...
          </p>
        ) : error ? (
          <p className="lb-message lb-error">{error}</p>
        ) : (
          <>
            <div className="lb-table-wrap">
              <table className="lb-table">
                <thead>
                  <tr>
                    <th>Rank</th>
                    <th>Player</th>
                    <th>Score</th>
                    <th>Time (min)</th>
                  </tr>
                </thead>
                <tbody>
                  {topFive.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="lb-empty">
                        No scores yet.
                      </td>
                    </tr>
                  ) : (
                    topFive.map((row) => {
                      const rank = Number(row.rank);
                      const isYou =
                        String(row.userId) === currentUserId;
                      return (
                        <tr
                          key={`${row.userId}-${rank}`}
                          className={getRowClass(rank, isYou)}
                        >
                          <td className="lb-rank">{rank}</td>
                          <td className="lb-name">
                            {getDisplayName(row)}
                            {isYou ? (
                              <span className="lb-you-tag"> (You)</span>
                            ) : null}
                          </td>
                          <td className="lb-score">{row.score ?? 0}</td>
                          <td className="lb-time">{row.time ?? "0"}</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {showCurrentUserRow && (
              <div className="lb-your-rank">
                <p className="lb-your-rank-label" style={{ color: textColor }}>
                  Your Rank
                </p>
                <div className="lb-your-rank-row">
                  <span className="lb-your-rank-num">
                    {currentUser.rank}
                  </span>
                  <span className="lb-your-rank-name">
                    {getDisplayName(currentUser)}
                  </span>
                  <span className="lb-your-rank-score">
                    {currentUser.score ?? 0} pts
                  </span>
                  <span className="lb-your-rank-time">
                    {currentUser.time ?? "0"} min
                  </span>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default Leaderboard;
